import { SessionToolbar } from "@/components/layout/SessionToolbar";
import { ActiveFilters } from "@/components/tools/ActiveFilters";
import { ToolControlPanel } from "@/components/tools/ToolControlPanel";
import {
	SCOTOMA_RADII,
	applyAnisoconiaToImageData,
	applyPostGridPipeline,
	applyPreDiploPipeline,
	buildFieldDefectMask,
} from "@/lib/image";
import { computePolygonMask } from "@/lib/image/regionMask";
import { useAuth, useUser } from "@clerk/clerk-react";
import type {
	EffectRegion,
	EffectRegionPolygon,
	FieldDefect,
	ScaleRegion,
	StoredEffectRegion,
	StoredFieldDefect,
} from "@client/types/vision";
import {
	AdjustmentsHorizontalIcon,
	ArrowPathIcon,
	CursorArrowRaysIcon,
	EyeIcon,
	EyeSlashIcon,
	LightBulbIcon,
	MoonIcon,
	SignalIcon,
	SparklesIcon,
	SwatchIcon,
} from "@heroicons/react/24/outline";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { DistortionPoint, ScotomaSpot } from "shared/schema/src/types";
import { toast } from "sonner";

// `hasRegion`: tool supports a "region" mode (effect applied to a chosen area
// of the face). Surfaced as a small badge in the sidebar so the capability is
// discoverable — it's otherwise hidden inside the tool's controls.
const TOOL_LIST = [
	{
		key: "blurriness",
		label: "Blurriness",
		icon: SparklesIcon,
		hasRegion: true,
	},
	{ key: "rotation", label: "Rotation", icon: ArrowPathIcon },
	{ key: "anisoconia", label: "Anisoconia", icon: EyeIcon, hasRegion: true },
	{
		key: "imageBrightness",
		label: "Image Brightness",
		icon: LightBulbIcon,
		hasRegion: true,
	},
	{
		key: "contrast",
		label: "Contrast",
		icon: AdjustmentsHorizontalIcon,
		hasRegion: true,
	},
	{ key: "colourShift", label: "Colour Shift", icon: SwatchIcon },
	{ key: "scotoma", label: "Scotoma", icon: EyeSlashIcon },
	{ key: "distortion", label: "Distortion", icon: CursorArrowRaysIcon },
	{ key: "vacuum", label: "Vacuum Effect", icon: CursorArrowRaysIcon },
	{
		key: "metamorphopsia",
		label: "Metamorphopsia",
		icon: SignalIcon,
		hasRegion: true,
	},
	{ key: "diplopia", label: "Diplopia", icon: EyeIcon },
	{ key: "fieldDefect", label: "Field Defect", icon: MoonIcon },
];

type ToolKey = (typeof TOOL_LIST)[number]["key"];

// Version of the CSV export naming + schema convention. Bump when filename
// pattern changes, columns are renamed/removed, units shift, or row ordering
// becomes load-bearing. Do NOT bump for new optional rows or formatting
// tweaks. Schema reference: specs/csv-export-schema.md.
const EXPORT_SCHEMA_VERSION = "v1";

const DEFAULT_EYE_STATE = {
	distortion: {
		points: [] as Array<{
			original: DistortionPoint;
			current: DistortionPoint;
		}>,
		draggingIndex: null as number | null,
		dragging: false,
		power: 1.0,
		range: 100,
		vacuumEffect: false,
		showImage: true,
	},
	// F12: Amsler grid distortion is a separate slice so editing the grid does
	// not warp the face (and vice versa). Same shape as `distortion`; defaults
	// clean. Only the post-grid distortion effect is split — vacuum and
	// metamorphopsia stay shared between grid and face.
	amslerDistortion: {
		points: [] as Array<{
			original: DistortionPoint;
			current: DistortionPoint;
		}>,
		draggingIndex: null as number | null,
		dragging: false,
		power: 1.0,
		range: 100,
		vacuumEffect: false,
		showImage: true,
	},
	imageBrightness: {
		value: 1,
		roiValue: 1,
		effectRegion: null as EffectRegion | null,
	},
	contrast: {
		level: 1,
		roiLevel: 1,
		effectRegion: null as EffectRegion | null,
	},
	blurriness: {
		sigma: 0,
		roiSigma: 0,
		effectRegion: null as EffectRegion | null,
	},
	scotoma: [] as ScotomaSpot[],
	colourShift: { hue: 0, saturation: 1 },
	rotation: { angle: 0 },
	diplopia: { enabled: false } as {
		enabled: boolean;
		offset?: { x: number; y: number };
		overlap?: number;
	},
	metamorphopsia: {
		enabled: false,
		amplitude: 0,
		frequency: 0,
		direction: "horizontal" as "horizontal" | "vertical" | "both",
		mode: "whole" as "whole" | "region",
		region: null as ScaleRegion | null,
		bridge: 0.5,
	},
	anisoconia: {
		enabled: false,
		headScale: 1,
		mode: "whole",
		region: null,
		scale: 1,
		bridge: 0.5,
	} as {
		enabled: boolean;
		headScale: number;
		mode: "whole" | "region";
		region: ScaleRegion | null;
		scale: number;
		bridge: number;
	},
	vacuum: {
		points: [] as Array<{
			original: DistortionPoint;
			current: DistortionPoint;
		}>,
		draggingIndex: null as number | null,
		dragging: false,
		power: 1.0,
		range: 100,
	},
	fieldDefect: null as FieldDefect | null,
};

// Polygon mask is a runtime-only Uint8Array (~1 MB at 1024^2). Drop before
// any persistence (server POST or localStorage WIP). Storage shape stays
// {type, points} for polygon, or the rect shape unchanged.
function stripRegion(
	r: EffectRegion | null | undefined,
): StoredEffectRegion | null {
	if (!r) return null;
	if (r.type === "polygon") {
		return { type: "polygon", points: r.points };
	}
	return r;
}

// Recompute the polygon mask from stored points after load. Existing rect
// data without a `type` field still loads correctly via the optional
// EffectRegionRect discriminator.
function hydrateRegion(
	r: unknown,
	imageWidth: number,
	imageHeight: number,
): EffectRegion | null {
	if (!r || typeof r !== "object") return null;
	const obj = r as Record<string, unknown>;
	if (obj.type === "polygon" && Array.isArray(obj.points)) {
		const points = obj.points as Array<{ x: number; y: number }>;
		return {
			type: "polygon",
			points,
			mask: computePolygonMask(points, imageWidth, imageHeight),
			maskWidth: imageWidth,
			maskHeight: imageHeight,
		};
	}
	return r as EffectRegion;
}

function stripEyeRegions<T extends Record<string, any>>(eye: T): T {
	return {
		...eye,
		blurriness: {
			...eye.blurriness,
			effectRegion: stripRegion(eye.blurriness?.effectRegion),
		},
		contrast: {
			...eye.contrast,
			effectRegion: stripRegion(eye.contrast?.effectRegion),
		},
		imageBrightness: {
			...eye.imageBrightness,
			effectRegion: stripRegion(eye.imageBrightness?.effectRegion),
		},
		fieldDefect: eye.fieldDefect
			? {
					mode: eye.fieldDefect.mode,
					points: eye.fieldDefect.points,
					line: eye.fieldDefect.line,
				}
			: null,
	} as T;
}

// F7: defaults for a freshly placed scotoma spot.
const DEFAULT_SCOTOMA_RADIUS = 60; // px (the former "M" preset)
const DEFAULT_SCOTOMA_OPACITY = 0.5; // matches the pre-F7 hardcoded darkness

// F8: undo. Changes less than UNDO_COALESCE_MS apart collapse into one undoable
// step (a slider sweep / distortion drag fires many updates with no drag-end
// event). UNDO_CAP bounds how many steps are retained; oldest drop first.
const UNDO_COALESCE_MS = 400;
const UNDO_CAP = 20;

function hydrateEyeRegions<T extends Record<string, any>>(
	eye: T,
	imageWidth: number,
	imageHeight: number,
): T {
	for (const tool of ["blurriness", "contrast", "imageBrightness"] as const) {
		const t = eye[tool] as Record<string, unknown> | undefined;
		if (t && "effectRegion" in t) {
			t.effectRegion = hydrateRegion(t.effectRegion, imageWidth, imageHeight);
		}
	}
	// F12: pre-F12 saves lack the Amsler distortion slice. New/loaded data gets
	// it via DEFAULT_EYE_STATE + the merge() backfill, but this also covers undo
	// snapshots and any partial eye object reaching render. Opens clean (no
	// fallback to the shared face distortion — David's call 2026-06-06).
	if (!(eye as Record<string, unknown>).amslerDistortion) {
		(eye as Record<string, unknown>).amslerDistortion = structuredClone(
			DEFAULT_EYE_STATE.amslerDistortion,
		);
	}
	// F7: normalize legacy scotoma spots ({ size: "S"|"M"|"L" } -> { radius }).
	// Pre-F7 spots stored a size code and an inert intensity (the renderer
	// hardcoded 0.5), so map the old size to a radius and pin intensity to that
	// effective 0.5. Spots already in the { radius } shape pass through untouched.
	const spots = (eye as Record<string, unknown>).scotoma;
	if (Array.isArray(spots)) {
		(eye as Record<string, unknown>).scotoma = (
			spots as Array<Record<string, unknown>>
		).map((s) =>
			typeof s.radius === "number"
				? s
				: {
						x: s.x,
						y: s.y,
						radius:
							s.size === "S" || s.size === "M" || s.size === "L"
								? SCOTOMA_RADII[s.size]
								: DEFAULT_SCOTOMA_RADIUS,
						intensity: 0.5,
					},
		);
	}
	// F9: rebuild the field-defect blackout mask from its stored line/points.
	const fd = (eye as Record<string, unknown>).fieldDefect as
		| StoredFieldDefect
		| null
		| undefined;
	if (fd) {
		const mask = buildFieldDefectMask(fd, imageWidth, imageHeight);
		(eye as Record<string, unknown>).fieldDefect = mask
			? { ...fd, mask, maskWidth: imageWidth, maskHeight: imageHeight }
			: fd;
	}
	return eye;
}

export default function IndexPage() {
	const [searchParams] = useSearchParams();
	const { user, isLoaded } = useUser();
	const { getToken } = useAuth();
	const authLoading = !isLoaded;
	const navigate = useNavigate();
	const [isSaving, setIsSaving] = useState(false);

	// Eye-specific filter states - MUST be declared before any early returns
	const [eyeStates, setEyeStates] = useState<{
		left: {
			distortion: {
				points: Array<{
					original: DistortionPoint;
					current: DistortionPoint;
				}>;
				draggingIndex: number | null;
				dragging: boolean;
				power: number;
				range: number;
				vacuumEffect: boolean;
				showImage: boolean;
			};
			amslerDistortion: {
				points: Array<{
					original: DistortionPoint;
					current: DistortionPoint;
				}>;
				draggingIndex: number | null;
				dragging: boolean;
				power: number;
				range: number;
				vacuumEffect: boolean;
				showImage: boolean;
			};
			imageBrightness: {
				value: number;
				roiValue?: number;
				effectRegion: EffectRegion | null;
			};
			contrast: {
				level: number;
				roiLevel?: number;
				preset?: "Low" | "Normal" | "High";
				mode?: "grayscale" | "warm" | "cool";
				effectRegion: EffectRegion | null;
			};
			blurriness: {
				sigma: number;
				roiSigma?: number;
				effectRegion: EffectRegion | null;
			};
			scotoma: ScotomaSpot[];
			colourShift: {
				hue: number;
				saturation: number;
				preset?: "Cyanopsia" | "Xanthopsia";
			};
			rotation: { angle: number };
			diplopia: {
				enabled: boolean;
				offset?: { x: number; y: number };
				overlap?: number;
			};
			metamorphopsia: {
				enabled: boolean;
				amplitude: number;
				frequency: number;
				direction: "horizontal" | "vertical" | "both";
				mode: "whole" | "region";
				region: ScaleRegion | null;
				bridge: number;
			};
			anisoconia: {
				enabled: boolean;
				headScale: number;
				mode: "whole" | "region";
				region: ScaleRegion | null;
				scale: number;
				bridge: number;
			};
			vacuum: {
				points: Array<{
					original: DistortionPoint;
					current: DistortionPoint;
				}>;
				draggingIndex: number | null;
				dragging: boolean;
				power: number;
				range: number;
			};
			fieldDefect: FieldDefect | null;
		};
		right: {
			distortion: {
				points: Array<{
					original: DistortionPoint;
					current: DistortionPoint;
				}>;
				draggingIndex: number | null;
				dragging: boolean;
				power: number;
				range: number;
				vacuumEffect: boolean;
				showImage: boolean;
			};
			amslerDistortion: {
				points: Array<{
					original: DistortionPoint;
					current: DistortionPoint;
				}>;
				draggingIndex: number | null;
				dragging: boolean;
				power: number;
				range: number;
				vacuumEffect: boolean;
				showImage: boolean;
			};
			imageBrightness: {
				value: number;
				roiValue?: number;
				effectRegion: EffectRegion | null;
			};
			contrast: {
				level: number;
				roiLevel?: number;
				preset?: "Low" | "Normal" | "High";
				mode?: "grayscale" | "warm" | "cool";
				effectRegion: EffectRegion | null;
			};
			blurriness: {
				sigma: number;
				roiSigma?: number;
				effectRegion: EffectRegion | null;
			};
			scotoma: ScotomaSpot[];
			colourShift: {
				hue: number;
				saturation: number;
				preset?: "Cyanopsia" | "Xanthopsia";
			};
			rotation: { angle: number };
			diplopia: {
				enabled: boolean;
				offset?: { x: number; y: number };
				overlap?: number;
			};
			metamorphopsia: {
				enabled: boolean;
				amplitude: number;
				frequency: number;
				direction: "horizontal" | "vertical" | "both";
				mode: "whole" | "region";
				region: ScaleRegion | null;
				bridge: number;
			};
			anisoconia: {
				enabled: boolean;
				headScale: number;
				mode: "whole" | "region";
				region: ScaleRegion | null;
				scale: number;
				bridge: number;
			};
			vacuum: {
				points: Array<{
					original: DistortionPoint;
					current: DistortionPoint;
				}>;
				draggingIndex: number | null;
				dragging: boolean;
				power: number;
				range: number;
			};
			fieldDefect: FieldDefect | null;
		};
	}>({
		left: { ...DEFAULT_EYE_STATE },
		right: { ...DEFAULT_EYE_STATE },
	});

	const assessmentId = searchParams.get("assessmentId");
	const [isViewMode, setIsViewMode] = useState(false);
	const [viewDate, setViewDate] = useState<string | null>(null);

	const handleToggleViewMode = useCallback(() => {
		setIsViewMode((prev) => !prev);
	}, []);

	const patientId = searchParams.get("patientId") ?? "";
	const patientCodeParam = searchParams.get("patientCode") ?? "";

	// Get patient data from localStorage (set by patients page)
	const storedPatient = localStorage.getItem("selectedPatient");
	const patient = storedPatient ? JSON.parse(storedPatient) : null;
	const patientCode = patient?.code ?? patientCodeParam;

	// Reference image + dimensions hoisted above the WIP / assessment-load
	// effects so polygon mask hydration can use the real image dimensions
	// instead of the 1024 fallback.
	const [referenceImage, setReferenceImage] = useState<HTMLImageElement | null>(
		null,
	);
	const imageWidth = referenceImage?.width || 1024;
	const imageHeight = referenceImage?.height || 1024;
	const [imageLoaded, setImageLoaded] = useState(false);

	// Auto-save WIP to localStorage (debounced 500ms)
	useEffect(() => {
		if (isViewMode) return; // Don't auto-save in view mode
		if (!patient?.id) return;

		const timer = setTimeout(() => {
			const stripped = {
				left: stripEyeRegions({
					...eyeStates.left,
					distortion: {
						...eyeStates.left.distortion,
						dragging: undefined,
						draggingIndex: undefined,
						showImage: undefined,
					},
					amslerDistortion: {
						...eyeStates.left.amslerDistortion,
						dragging: undefined,
						draggingIndex: undefined,
						showImage: undefined,
					},
					vacuum: {
						...eyeStates.left.vacuum,
						dragging: undefined,
						draggingIndex: undefined,
					},
				}),
				right: stripEyeRegions({
					...eyeStates.right,
					distortion: {
						...eyeStates.right.distortion,
						dragging: undefined,
						draggingIndex: undefined,
						showImage: undefined,
					},
					amslerDistortion: {
						...eyeStates.right.amslerDistortion,
						dragging: undefined,
						draggingIndex: undefined,
						showImage: undefined,
					},
					vacuum: {
						...eyeStates.right.vacuum,
						dragging: undefined,
						draggingIndex: undefined,
					},
				}),
			};
			try {
				localStorage.setItem(
					`assessment_wip_${patient.id}`,
					JSON.stringify(stripped),
				);
			} catch (err) {
				// Quota exceeded or storage disabled. Don't block the session;
				// the explicit Save button still POSTs to the server.
				console.warn("WIP autosave to localStorage failed", err);
			}
		}, 500);

		return () => clearTimeout(timer);
	}, [eyeStates, patient?.id, isViewMode]);

	// Restore WIP from localStorage on mount.
	// Gated on imageLoaded so polygon mask hydration uses the real image
	// dimensions, not the 1024 fallback.
	useEffect(() => {
		if (assessmentId) return; // Don't restore WIP in view mode
		if (!patient?.id) return;
		if (!imageLoaded) return;

		const saved = localStorage.getItem(`assessment_wip_${patient.id}`);
		if (!saved) return;

		try {
			const parsed = JSON.parse(saved);
			setEyeStates((prev) => {
				const merge = (
					defaults: Record<string, unknown>,
					saved: Record<string, unknown> | undefined,
				) => {
					if (!saved) return defaults;
					const result: Record<string, unknown> = {};
					for (const key of Object.keys(defaults)) {
						if (
							saved[key] !== undefined &&
							typeof defaults[key] === "object" &&
							!Array.isArray(defaults[key]) &&
							defaults[key] !== null
						) {
							result[key] = {
								...(defaults[key] as Record<string, unknown>),
								...(saved[key] as Record<string, unknown>),
							};
						} else if (saved[key] !== undefined) {
							result[key] = saved[key];
						} else {
							result[key] = defaults[key];
						}
					}
					return result;
				};
				return {
					left: hydrateEyeRegions(
						merge(prev.left, parsed.left) as Record<string, any>,
						imageWidth,
						imageHeight,
					),
					right: hydrateEyeRegions(
						merge(prev.right, parsed.right) as Record<string, any>,
						imageWidth,
						imageHeight,
					),
				} as typeof prev;
			});
		} catch {
			// Corrupt data — ignore
		}
	}, [patient?.id, imageLoaded, imageWidth, imageHeight]);

	// Load a saved assessment when assessmentId is in the URL (view mode).
	// Gated on imageLoaded so polygon mask hydration uses real image dimensions
	// instead of the 1024 fallback (which is correct today only by coincidence).
	useEffect(() => {
		if (!assessmentId) return;
		if (!imageLoaded) return;

		async function loadAssessment() {
			try {
				const token = await getToken();
				const res = await fetch(
					`${import.meta.env.VITE_BASE_API_URL}/assessments/${assessmentId}`,
					{ headers: { Authorization: `Bearer ${token}` } },
				);
				if (!res.ok) throw new Error("Failed to load assessment");
				const data = await res.json();

				// Set the eye states from the saved assessment.
				setEyeStates((prev) => {
					const merge = (
						defaults: Record<string, unknown>,
						saved: Record<string, unknown> | undefined,
					) => {
						if (!saved) return defaults;
						const result: Record<string, unknown> = {};
						for (const key of Object.keys(defaults)) {
							if (
								saved[key] !== undefined &&
								typeof defaults[key] === "object" &&
								!Array.isArray(defaults[key]) &&
								defaults[key] !== null
							) {
								result[key] = {
									...(defaults[key] as Record<string, unknown>),
									...(saved[key] as Record<string, unknown>),
								};
							} else if (saved[key] !== undefined) {
								result[key] = saved[key];
							} else {
								result[key] = defaults[key];
							}
						}
						return result;
					};
					return {
						left: hydrateEyeRegions(
							merge(prev.left, data.eyeStates?.left) as Record<string, any>,
							imageWidth,
							imageHeight,
						),
						right: hydrateEyeRegions(
							merge(prev.right, data.eyeStates?.right) as Record<string, any>,
							imageWidth,
							imageHeight,
						),
					} as typeof prev;
				});

				setIsViewMode(true);
				setViewDate(data.createdAt);

				// Also store patient data if available from the assessment response
				if (data.patient) {
					localStorage.setItem("selectedPatient", JSON.stringify(data.patient));
				}
			} catch (err: unknown) {
				const message =
					err instanceof Error ? err.message : "Failed to load assessment";
				toast.error(message);
			}
		}

		loadAssessment();
	}, [assessmentId, imageLoaded, imageWidth, imageHeight]);

	const [currentTool, setCurrentTool] = useState<ToolKey | null>(null);
	const [eye, setEye] = useState<"left" | "right">("left");
	// F10/F11: transient in-progress gesture for the shared ellipse region
	// (anisoconia + metamorphopsia). Eye-scoped UI state — reset on eye/tool switch.
	const ellipseRegionDragRef = useRef<{
		mode: "move" | "resize";
		ox: number;
		oy: number;
	} | null>(null);

	// F12: the distortion slice (Amsler vs face) pinned at drag start. The
	// move/up handlers close over a fresh distortionField each render, so a
	// gridMode switch mid-drag (e.g. a second finger taps the mode toggle on
	// iPad) would otherwise move/clear the wrong slice and strand dragging=true
	// on the slice the drag began on. Pin at mousedown, use it through mouseup.
	const dragDistortionFieldRef = useRef<"distortion" | "amslerDistortion">(
		"distortion",
	);

	// F10/F11: which tool (if any) is currently in ellipse-region mode. Both
	// Anisoconia and Metamorphopsia share the tap/move/resize gesture.
	const activeEllipseRegionTool = ():
		| "anisoconia"
		| "metamorphopsia"
		| null => {
		const es = getCurrentEyeState();
		if (currentTool === "anisoconia" && es.anisoconia.mode === "region")
			return "anisoconia";
		if (currentTool === "metamorphopsia" && es.metamorphopsia.mode === "region")
			return "metamorphopsia";
		return null;
	};

	// Type-safe per-tool region write, kept in one place so the gesture handlers
	// stay tool-generic.
	const setEllipseRegion = (
		tool: "anisoconia" | "metamorphopsia",
		updater: (r: ScaleRegion | null) => ScaleRegion | null,
	) => {
		setCurrentEyeState((prev) =>
			tool === "anisoconia"
				? {
						...prev,
						anisoconia: {
							...prev.anisoconia,
							region: updater(prev.anisoconia.region),
						},
					}
				: {
						...prev,
						metamorphopsia: {
							...prev.metamorphopsia,
							region: updater(prev.metamorphopsia.region ?? null),
						},
					},
		);
	};

	// Helper functions to get and set current eye state
	const getCurrentEyeState = useCallback(
		() => eyeStates[eye],
		[eyeStates, eye],
	);

	// F8: undo history. A mask-free, deep-cloned snapshot of both eyes is pushed
	// onto the stack at the start of each change-burst (coalesced by time).
	const [canUndo, setCanUndo] = useState(false);
	const undoStackRef = useRef<Array<typeof eyeStates>>([]);
	const lastMutationTimeRef = useRef(0);
	const eyeStatesRef = useRef(eyeStates);
	useEffect(() => {
		eyeStatesRef.current = eyeStates;
	}, [eyeStates]);

	const pushUndoSnapshot = useCallback(() => {
		const live = eyeStatesRef.current;
		// Strip the ~1MB polygon masks, then deep-clone so later immutable updates
		// can't alias the snapshot. Masks are recomputed on undo via hydrate.
		const snapshot = JSON.parse(
			JSON.stringify({
				left: stripEyeRegions(live.left),
				right: stripEyeRegions(live.right),
			}),
		);
		undoStackRef.current.push(snapshot);
		if (undoStackRef.current.length > UNDO_CAP) undoStackRef.current.shift();
		setCanUndo(true);
	}, []);

	const setCurrentEyeState = useCallback(
		(updater: (prev: typeof eyeStates.left) => typeof eyeStates.left) => {
			const now = Date.now();
			if (now - lastMutationTimeRef.current > UNDO_COALESCE_MS) {
				pushUndoSnapshot();
			}
			lastMutationTimeRef.current = now;
			setEyeStates((prev) => ({
				...prev,
				[eye]: updater(prev[eye]),
			}));
		},
		[eye, pushUndoSnapshot],
	);

	const handleUndo = useCallback(() => {
		const snapshot = undoStackRef.current.pop();
		if (!snapshot) return;
		const restored = {
			left: hydrateEyeRegions(snapshot.left, imageWidth, imageHeight),
			right: hydrateEyeRegions(snapshot.right, imageWidth, imageHeight),
		} as typeof eyeStates;
		eyeStatesRef.current = restored;
		setEyeStates(restored);
		// A restored snapshot may not contain the previously-selected scotoma spot.
		setSelectedScotomaIndex(null);
		setCanUndo(undoStackRef.current.length > 0);
		lastMutationTimeRef.current = 0; // next change starts a fresh step
	}, [imageWidth, imageHeight]);
	const [isPortrait, setIsPortrait] = useState(false);
	const date = new Date().toISOString().slice(0, 10);

	// (referenceImage / imageWidth / imageHeight / imageLoaded are hoisted
	// above the WIP and assessment-load effects.)

	// Add this above the return statement in IndexPage
	const [testHovered, setTestHovered] = useState(false);

	// Set document title for web browser and manage scrolling based on screen size
	useEffect(() => {
		if (typeof document !== "undefined") {
			document.title = "St Michael's Unity Health Eye App";

			// Set favicon
			const faviconLink =
				document.querySelector('link[rel="icon"]') ||
				document.createElement("link");
			faviconLink.setAttribute("rel", "icon");
			faviconLink.setAttribute("type", "image/jpeg");
			faviconLink.setAttribute("href", "/images/tab-logo.jpg");
			if (!document.querySelector('link[rel="icon"]')) {
				document.head.appendChild(faviconLink);
			}

			// Function to handle scrolling based on screen size
			const handleScrollBehavior = () => {
				const isLargeScreen = window.innerWidth >= 1200; // xl breakpoint for true desktop monitors
				if (isLargeScreen) {
					// Allow scrolling on large screens (desktop monitors)
					document.body.style.overflow = "auto";
					document.body.style.height = "auto";
					document.documentElement.style.overflow = "auto";
					document.documentElement.style.height = "auto";
				} else {
					// Prevent scrolling on smaller screens (tablets, phones, small laptops)
					document.body.style.overflow = "hidden";
					document.body.style.height = "100vh";
					document.documentElement.style.overflow = "hidden";
					document.documentElement.style.height = "100vh";
				}
			};

			// Function to detect orientation
			const handleOrientation = () => {
				const isPortraitMode = window.innerHeight > window.innerWidth;
				setIsPortrait(isPortraitMode);
			};

			// Set initial behavior
			handleScrollBehavior();
			handleOrientation();

			// Listen for window resize events
			window.addEventListener("resize", () => {
				handleScrollBehavior();
				handleOrientation();
			});

			// Listen for orientation change events (for mobile devices)
			window.addEventListener("orientationchange", handleOrientation);

			// Cleanup function
			return () => {
				window.removeEventListener("resize", () => {
					handleScrollBehavior();
					handleOrientation();
				});
				window.removeEventListener("orientationchange", handleOrientation);
			};
		}
	}, []);

	// Legacy filter states (will be removed after migration)
	const [distortion, setDistortion] = useState<{
		points: Array<{
			original: DistortionPoint;
			current: DistortionPoint;
		}>;
		draggingIndex: number | null;
		dragging: boolean;
		power: number;
		range: number;
		vacuumEffect: boolean;
		showImage: boolean;
	}>({
		points: [],
		draggingIndex: null,
		dragging: false,
		power: 1.0,
		range: 100,
		vacuumEffect: false,
		showImage: true,
	});
	const [imageBrightness, setImageBrightness] = useState<number>(1);
	const [contrast, setContrast] = useState<{
		level: number;
		preset?: "Low" | "Normal" | "High";
		mode?: "grayscale" | "warm" | "cool";
	}>({ level: 1 });

	const [blurriness, setBlurriness] = useState<{ sigma: number }>({ sigma: 0 });
	const [scotoma, setScotoma] = useState<ScotomaSpot[]>([]);
	const [colourShift, setColourShift] = useState<{
		hue: number;
		saturation: number;
		preset?: "Cyanopsia" | "Xanthopsia";
	}>({ hue: 0, saturation: 1 });
	const [rotation, setRotation] = useState<{ angle: number }>({ angle: 0 });
	const [diplopia, setDiplopia] = useState<{
		enabled: boolean;
		offset?: { x: number; y: number };
		overlap?: number;
	}>({ enabled: false });
	const [anisoconia, setAnisoconia] = useState<{
		enabled: boolean;
		headScale: number;
	}>({ enabled: false, headScale: 1 });

	const imageContainerRef = useRef<HTMLDivElement>(null);

	// Filter summary state
	const [disabledFilters, setDisabledFilters] = useState<Set<string>>(
		new Set(),
	);

	// Add at the top of the component
	const filteredCanvasRef = useRef<HTMLCanvasElement>(null);
	const originalImageCanvasRef = useRef<HTMLCanvasElement>(null);

	// Replace the <img> with a visible <canvas> that displays the filteredCanvasRef output
	const visibleCanvasRef = useRef<HTMLCanvasElement>(null);

	// True once the paint useLayoutEffect has drawn the visible canvas at least
	// once. The visible-canvas bootstrap useEffect uses this to skip overwriting
	// the filtered output with a plain reference image after paint already ran.
	// Required for the F1 refresh-paint bug: when WIP-restored filters are
	// already in eyeStates by the time auth resolves, paint runs first and the
	// bootstrap would otherwise erase its output on the same render commit.
	const paintRanRef = useRef(false);

	// F7: index of the scotoma spot currently being edited via the sliders.
	// Runtime-only (not persisted). null = nothing selected.
	const [selectedScotomaIndex, setSelectedScotomaIndex] = useState<
		number | null
	>(null);

	// Grid mode (global for all tools): off, image overlay, or standalone Amsler grid
	const [gridMode, setGridMode] = useState<"off" | "overlay" | "amsler">("off");
	const showGrid = gridMode !== "off";
	// F12: the distortion tool edits the Amsler slice only in true Amsler mode;
	// "overlay" still renders the face, so it writes the face slice. All
	// distortion reads/writes route through this key.
	const distortionField: "distortion" | "amslerDistortion" =
		gridMode === "amsler" ? "amslerDistortion" : "distortion";
	const [gridLineWidth, setGridLineWidth] = useState(2);

	// Polygon ROI drawing state. Tap to add points, close by tap-near-start
	// or double-tap. Min 3 points to close. Vertex circles ship at 22px
	// radius for senior-friendly tap targets.
	const [regionDrawMode, setRegionDrawMode] = useState<"none" | "drawing">(
		"none",
	);
	const [regionPoints, setRegionPoints] = useState<
		Array<{ x: number; y: number }>
	>([]);
	const [lastTapTime, setLastTapTime] = useState(0);
	// F9 field defect: the curtain/tunnel toggle, plus the in-progress curtain
	// first tap (curtain is a 2-tap line; tunnel reuses the polygon engine).
	const [fieldDefectMode, setFieldDefectMode] = useState<"tunnel" | "curtain">(
		"curtain",
	);
	const [curtainStart, setCurtainStart] = useState<{
		x: number;
		y: number;
	} | null>(null);

	// --- Load face image for distortion testing ---
	const [faceImage, setFaceImage] = useState<HTMLImageElement | null>(null);

	// --- Load the reference image once on mount ---
	useEffect(() => {
		const img = new window.Image();
		img.src = "/images/reference.png";
		img.onload = () => {
			setReferenceImage(img);
			setImageLoaded(true);
		};
		img.onerror = () => {
			console.error("Failed to load reference image:", img.src);
		};
	}, []);
	useEffect(() => {
		if (!imageLoaded || !referenceImage) return;

		const img = new window.Image();
		img.src = "/images/face-test.png"; // Add a face image to client/public/images/face-test.jpg
		img.onload = () => {
			setFaceImage(img);
		};
		img.onerror = () => {
			console.log("Face image not found, using reference image as fallback");
			// Fallback to reference image if face image not found
			setFaceImage(referenceImage);
		};
	}, [imageLoaded, referenceImage]);

	// Initialize the original image canvas once
	// Note: authLoading is in the deps because the canvas refs are null while
	// the auth guard renders <Loading /> — when auth finishes, the canvases
	// mount and this effect needs to re-fire to draw the image.
	useEffect(() => {
		if (!imageLoaded || !referenceImage) return;
		const canvas = originalImageCanvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d", { willReadFrequently: true });
		if (!ctx) return;
		ctx.clearRect(0, 0, 1024, 1024);
		ctx.drawImage(referenceImage, 0, 0, 1024, 1024);
	}, [imageLoaded, referenceImage, authLoading]);

	// Ensure the reference image is drawn to the canvas on first load.
	// (Previously removed in PR #19 because it appeared redundant with the
	// useLayoutEffect below. Production showed a regression: without this
	// bootstrap, the canvas stayed blank entirely on first paint instead of
	// showing the unfiltered face. The original "filters not applied on
	// refresh" bug returns until a proper diagnosis lands.)
	useEffect(() => {
		if (!imageLoaded || !referenceImage) return;
		const filteredCanvas = filteredCanvasRef.current;
		const visibleCanvas = visibleCanvasRef.current;
		if (!filteredCanvas || !visibleCanvas) return;
		// If the paint useLayoutEffect has already drawn the visible canvas
		// (with WIP-restored filters), skip drawing the plain reference. Without
		// this guard the bootstrap fires after paint and erases filtered output
		// on the same render — the F1 refresh-paint bug.
		if (paintRanRef.current) return;

		const filteredCtx = filteredCanvas.getContext("2d", {
			willReadFrequently: true,
		});
		if (filteredCtx) {
			filteredCtx.clearRect(0, 0, 1024, 1024);
			filteredCtx.drawImage(referenceImage, 0, 0, 1024, 1024);
		}

		const visibleCtx = visibleCanvas.getContext("2d", {
			willReadFrequently: true,
		});
		if (visibleCtx) {
			visibleCtx.clearRect(0, 0, 1024, 1024);
			visibleCtx.drawImage(referenceImage, 0, 0, 1024, 1024);
		}
	}, [imageLoaded, referenceImage, authLoading]);

	// Helper to get filters without blur (for polygon blur effect)
	const getFiltersWithoutBlur = useCallback(() => {
		const filters: string[] = [];

		// Note: Contrast is now applied directly to pixels, not via CSS filter
		// Note: Colour shift is now applied directly to pixels, not via CSS filter
		// Note: Brightness is now applied directly to pixels, not via CSS filter

		return filters.join(" ");
	}, []);

	// Vacuum effect function (separated from distortion)

	// Single useLayoutEffect to update the visible canvas with all effects applied
	useLayoutEffect(() => {
		if (!imageLoaded || !referenceImage) return;
		const canvas = visibleCanvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d", { willReadFrequently: true });
		if (!ctx) return;

		// Get current eye state for image processing
		const currentState = getCurrentEyeState();

		const drawGridLines = (color: string) => {
			const cells = 20;
			ctx.save();
			ctx.strokeStyle = color;
			ctx.lineWidth = gridLineWidth;
			for (let i = 0; i <= cells; i++) {
				const y = (i / cells) * imageHeight;
				ctx.beginPath();
				ctx.moveTo(0, y);
				ctx.lineTo(imageWidth, y);
				ctx.stroke();
			}
			for (let j = 0; j <= cells; j++) {
				const x = (j / cells) * imageWidth;
				ctx.beginPath();
				ctx.moveTo(x, 0);
				ctx.lineTo(x, imageHeight);
				ctx.stroke();
			}
			ctx.restore();
		};

		// Amsler grid mode: black canvas + white grid + red center fixation dot.
		// Skips pre-grid pipeline (no image), runs post-grid pipeline so
		// metamorphopsia/distortion/vacuum can still warp the grid lines.
		if (gridMode === "amsler") {
			ctx.filter = "none";
			ctx.fillStyle = "#000";
			ctx.fillRect(0, 0, imageWidth, imageHeight);
			drawGridLines("rgba(255, 255, 255, 0.9)");
			ctx.fillStyle = "rgba(255, 0, 0, 1)";
			ctx.beginPath();
			ctx.arc(imageWidth / 2, imageHeight / 2, 4, 0, Math.PI * 2);
			ctx.fill();
			const amslerImageData = ctx.getImageData(0, 0, imageWidth, imageHeight);
			const amslerDistorted = applyPostGridPipeline(
				amslerImageData,
				{
					metamorphopsia: currentState.metamorphopsia,
					// F12: Amsler grid reads its own distortion slice. vacuum +
					// metamorphopsia remain shared with the face (scope decision).
					distortion: currentState.amslerDistortion,
					vacuum: currentState.vacuum,
				},
				disabledFilters,
			);
			ctx.putImageData(amslerDistorted, 0, 0);
			return;
		}

		ctx.clearRect(0, 0, imageWidth, imageHeight);
		ctx.filter = getFiltersWithoutBlur();

		// Draw the base image (face or reference)
		if (currentState.distortion.showImage) {
			ctx.drawImage(referenceImage, 0, 0, imageWidth, imageHeight);
		} else {
			// Fill with white when image is hidden
			ctx.fillStyle = "#fff";
			ctx.fillRect(0, 0, imageWidth, imageHeight);
		}

		// PERFORMANCE OPTIMIZATION: Batch all pre-diplopia filters into a single GPU readback
		// This reduces 7 sequential getImageData() calls to just 1, saving ~90-450ms per frame
		const imageData = ctx.getImageData(0, 0, imageWidth, imageHeight);
		const processedData = applyPreDiploPipeline(
			imageData,
			{
				rotation: currentState.rotation,
				colourShift: currentState.colourShift,
				contrast: currentState.contrast,
				imageBrightness: currentState.imageBrightness,
				scotoma: currentState.scotoma,
				blurriness: currentState.blurriness,
				fieldDefect: currentState.fieldDefect,
				// F10: region scale only renders in region mode (mutually
				// exclusive with the whole-head pass below).
				regionScale:
					currentState.anisoconia.mode === "region" &&
					currentState.anisoconia.region
						? {
								region: currentState.anisoconia.region,
								scale: currentState.anisoconia.scale ?? 1,
								bridge: currentState.anisoconia.bridge ?? 0.5,
							}
						: null,
			},
			disabledFilters,
		);
		ctx.putImageData(processedData, 0, 0);

		// --- Composite Diplopia Effect ---
		if (!disabledFilters.has("diplopia") && currentState.diplopia.enabled) {
			ctx.save();
			ctx.globalAlpha = currentState.diplopia.overlap
				? currentState.diplopia.overlap / 100
				: 0.5;
			ctx.setTransform(
				1,
				0,
				0,
				1,
				currentState.diplopia.offset?.x || 0,
				currentState.diplopia.offset?.y || 0,
			);
			ctx.drawImage(canvas, 0, 0, imageWidth, imageHeight);
			ctx.restore();
		}

		// Apply anisoconia (whole-head) BEFORE grid to avoid sampling green grid
		// pixels. Gated to whole mode so a stored headScale doesn't double-apply
		// under a region scale (region renders in the pre-diplo pipeline above).
		if (
			!disabledFilters.has("anisoconia") &&
			currentState.anisoconia.mode !== "region" &&
			currentState.anisoconia.headScale !== 1
		) {
			const imageData = ctx.getImageData(0, 0, imageWidth, imageHeight);
			const adjustedImageData = applyAnisoconiaToImageData(
				imageData,
				currentState.anisoconia.headScale,
			);
			ctx.putImageData(adjustedImageData, 0, 0);
		}

		// Draw the mesh grid AFTER anisoconia but BEFORE distortion if enabled globally
		if (showGrid) {
			drawGridLines("rgba(144, 238, 144, 0.9)");
		}

		// PERFORMANCE OPTIMIZATION: Batch distortion and vacuum into a single GPU readback
		// These must be applied after the grid so the grid gets distorted with the image
		const postGridImageData = ctx.getImageData(0, 0, imageWidth, imageHeight);
		const distortedData = applyPostGridPipeline(
			postGridImageData,
			{
				metamorphopsia: currentState.metamorphopsia,
				distortion: currentState.distortion,
				vacuum: currentState.vacuum,
			},
			disabledFilters,
		);
		ctx.putImageData(distortedData, 0, 0);
		paintRanRef.current = true;
	}, [
		imageLoaded,
		referenceImage,
		disabledFilters,
		gridMode,
		showGrid,
		gridLineWidth,
		getCurrentEyeState,
		getFiltersWithoutBlur,
		imageWidth,
		imageHeight,
		// Required for the F1 refresh-paint bug: canvases mount only after auth
		// resolves (the auth gate renders <Loading /> until then). Without this
		// dep, paint never re-fires when refs become valid, the visible-bootstrap
		// useEffect runs alone, and the canvas shows the unfiltered face.
		authLoading,
	]);

	// Tool renderer
	const renderTool = () => {
		const currentState = getCurrentEyeState();
		return (
			<ToolControlPanel
				currentTool={currentTool as any}
				eyeState={currentState}
				distortionField={distortionField}
				selectedScotomaIndex={selectedScotomaIndex}
				onSelectScotoma={setSelectedScotomaIndex}
				fieldDefectMode={fieldDefectMode}
				curtainAwaitingSecondTap={curtainStart !== null}
				onFieldDefectModeChange={(m) => {
					setFieldDefectMode(m);
					setCurtainStart(null);
					setRegionDrawMode("none");
					setRegionPoints([]);
					setCurrentEyeState((prev) => ({ ...prev, fieldDefect: null }));
				}}
				onFlipFieldDefectSide={() => {
					setCurrentEyeState((prev) => {
						const fd = prev.fieldDefect;
						if (!fd?.line) return prev;
						const side: "a" | "b" = fd.line.side === "a" ? "b" : "a";
						const stored: StoredFieldDefect = {
							mode: "curtain",
							line: { ...fd.line, side },
						};
						const mask = buildFieldDefectMask(stored, imageWidth, imageHeight);
						return {
							...prev,
							fieldDefect: mask
								? {
										...stored,
										mask,
										maskWidth: imageWidth,
										maskHeight: imageHeight,
									}
								: null,
						};
					});
				}}
				onClearFieldDefect={() => {
					setCurtainStart(null);
					setCurrentEyeState((prev) => ({ ...prev, fieldDefect: null }));
				}}
				onEyeStateChange={setCurrentEyeState}
				visibleCanvasRef={visibleCanvasRef}
				imageLoaded={imageLoaded}
				referenceImage={referenceImage}
				imageWidth={imageWidth}
				imageHeight={imageHeight}
				showGrid={showGrid}
				regionDrawMode={regionDrawMode}
				regionPoints={regionPoints}
				onStartDrawing={() => {
					// Clear any existing region for the current tool so the in-progress
					// polyline doesn't overlap a stale closed polygon.
					setCurrentEyeState((prev) => {
						if (currentTool === "blurriness") {
							return {
								...prev,
								blurriness: { ...prev.blurriness, effectRegion: null },
							};
						}
						if (currentTool === "contrast") {
							return {
								...prev,
								contrast: { ...prev.contrast, effectRegion: null },
							};
						}
						if (currentTool === "imageBrightness") {
							return {
								...prev,
								imageBrightness: {
									...prev.imageBrightness,
									effectRegion: null,
								},
							};
						}
						if (currentTool === "fieldDefect") {
							return { ...prev, fieldDefect: null };
						}
						return prev;
					});
					setRegionDrawMode("drawing");
					setRegionPoints([]);
					setLastTapTime(0);
				}}
				onUndoPoint={() => {
					setRegionPoints((prev) => prev.slice(0, -1));
				}}
				onCancelDrawing={() => {
					setRegionDrawMode("none");
					setRegionPoints([]);
					setLastTapTime(0);
				}}
				onClearRegion={() => {
					// Clearing the region also resets the ROI boost slider value to
					// its safe default so the now-hidden second slider does not
					// surprise the user when they redraw.
					setCurrentEyeState((prev) => {
						if (currentTool === "blurriness") {
							return {
								...prev,
								blurriness: {
									...prev.blurriness,
									roiSigma: 0,
									effectRegion: null,
								},
							};
						}
						if (currentTool === "contrast") {
							return {
								...prev,
								contrast: {
									...prev.contrast,
									roiLevel: 1,
									effectRegion: null,
								},
							};
						}
						if (currentTool === "imageBrightness") {
							return {
								...prev,
								imageBrightness: {
									...prev.imageBrightness,
									roiValue: 1,
									effectRegion: null,
								},
							};
						}
						return prev;
					});
					setRegionDrawMode("none");
					setRegionPoints([]);
				}}
				disabled={isViewMode}
			/>
		);
	};

	// Mouse handlers for area selection
	// Helper function to get coordinates from mouse or touch event
	const getEventCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
		const rect = imageContainerRef.current?.getBoundingClientRect();
		if (!rect) return null;

		let clientX: number;
		let clientY: number;

		if ("touches" in e) {
			// Touch event
			if (e.touches.length === 0) return null;
			const touch = e.touches[0];
			if (!touch) return null;
			clientX = touch.clientX;
			clientY = touch.clientY;

			// Touch events are working correctly
		} else {
			// Mouse event
			clientX = e.clientX;
			clientY = e.clientY;
		}

		// Account for scaling - convert from display coordinates to image coordinates
		// Use canvas dimensions instead of container dimensions for accurate scaling
		const x = ((clientX - rect.left) / canvasDimensions.width) * 1024;
		const y = ((clientY - rect.top) / canvasDimensions.height) * 1024;

		// Coordinates calculated successfully

		return { x, y };
	};

	const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
		// Ignore mouse events that come after touch events (synthetic events)
		if (!("touches" in e) && touchActiveRef.current) {
			console.log("Ignoring synthetic mouse event after touch");
			return;
		}

		// Handle panning when zoomed in and no tool is selected
		if (currentTool === null && imageZoom > 1) {
			const clientX = "touches" in e ? (e.touches[0]?.clientX ?? 0) : e.clientX;
			const clientY = "touches" in e ? (e.touches[0]?.clientY ?? 0) : e.clientY;
			setIsPanning(true);
			setPanStart({ x: clientX - panOffset.x, y: clientY - panOffset.y });
			return;
		}

		if (isViewMode) return;

		const coords = getEventCoordinates(e);
		if (!coords) return;

		if (currentTool === "distortion") {
			const currentState = getCurrentEyeState();
			// Create new point and start dragging immediately
			const newPoint = { x: coords.x, y: coords.y };
			// F12: route to the active distortion slice (Amsler vs face) and pin
			// it for the rest of this drag (survives a mid-drag gridMode switch).
			dragDistortionFieldRef.current = distortionField;
			const newPointIndex = currentState[distortionField].points.length;
			setCurrentEyeState((prev) => ({
				...prev,
				[distortionField]: {
					...prev[distortionField],
					points: [
						...prev[distortionField].points,
						{ original: newPoint, current: newPoint },
					],
					draggingIndex: newPointIndex,
					dragging: true,
				},
			}));
		} else if (currentTool === "vacuum") {
			console.log("Adding vacuum point (handleMouseDown):", coords);
			const currentState = getCurrentEyeState();
			// For vacuum, do not allow dragging, just shrink at click
			const newPoint = { x: coords.x, y: coords.y };
			setCurrentEyeState((prev) => ({
				...prev,
				vacuum: {
					...prev.vacuum,
					points: [
						...prev.vacuum.points,
						{ original: newPoint, current: newPoint },
					],
					draggingIndex: null,
					dragging: false,
				},
			}));
		} else {
			// F10/F11: shared ellipse region (Anisoconia + Metamorphopsia) —
			// tap to place / move / resize.
			const ellipseTool = activeEllipseRegionTool();
			if (ellipseTool) {
				const r = getCurrentEyeState()[ellipseTool].region ?? null;
				if (r) {
					// Corner handle (bottom-right of the ellipse bbox) → resize.
					if (
						Math.hypot(coords.x - (r.cx + r.rx), coords.y - (r.cy + r.ry)) < 50
					) {
						ellipseRegionDragRef.current = { mode: "resize", ox: 0, oy: 0 };
						return;
					}
					// Inside the ellipse → move.
					const nx = (coords.x - r.cx) / r.rx;
					const ny = (coords.y - r.cy) / r.ry;
					if (nx * nx + ny * ny <= 1) {
						ellipseRegionDragRef.current = {
							mode: "move",
							ox: coords.x - r.cx,
							oy: coords.y - r.cy,
						};
						return;
					}
				}
				// Otherwise drop a new ellipse centered on the tap (default size in
				// image space; keep the prior size if one existed) and begin a move.
				// Metamorphopsia waves read better in a larger default area.
				const [defRx, defRy] =
					ellipseTool === "metamorphopsia" ? [140, 110] : [110, 84];
				ellipseRegionDragRef.current = { mode: "move", ox: 0, oy: 0 };
				setEllipseRegion(ellipseTool, (prev) => ({
					cx: coords.x,
					cy: coords.y,
					rx: prev?.rx ?? defRx,
					ry: prev?.ry ?? defRy,
				}));
			}
		}
	};

	const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
		// Handle panning when zoomed in
		if (isPanning && imageZoom > 1) {
			const clientX = "touches" in e ? (e.touches[0]?.clientX ?? 0) : e.clientX;
			const clientY = "touches" in e ? (e.touches[0]?.clientY ?? 0) : e.clientY;

			const newX = clientX - panStart.x;
			const newY = clientY - panStart.y;

			// Calculate maximum pan distance based on zoom level
			const maxPanX = (canvasDimensions.width * (imageZoom - 1)) / 2;
			const maxPanY = (canvasDimensions.height * (imageZoom - 1)) / 2;

			// Clamp pan offset to prevent panning beyond image bounds
			const clampedX = Math.max(-maxPanX, Math.min(maxPanX, newX));
			const clampedY = Math.max(-maxPanY, Math.min(maxPanY, newY));

			setPanOffset({ x: clampedX, y: clampedY });
			return;
		}

		if (isViewMode) return;

		const coords = getEventCoordinates(e);
		if (!coords) return;

		if (
			currentTool === "distortion" &&
			getCurrentEyeState()[dragDistortionFieldRef.current].dragging &&
			getCurrentEyeState()[dragDistortionFieldRef.current].draggingIndex !==
				null
		) {
			// F12: act on the slice pinned at drag start, not the current mode.
			const field = dragDistortionFieldRef.current;
			const currentState = getCurrentEyeState();
			console.log(
				"Distortion tool - dragging point:",
				currentState[field].draggingIndex,
				"to:",
				coords.x,
				coords.y,
			); // Debug
			// Update the dragging point position
			setCurrentEyeState((prev) => ({
				...prev,
				[field]: {
					...prev[field],
					points: prev[field].points.map((point, index) =>
						index === prev[field].draggingIndex
							? { ...point, current: { x: coords.x, y: coords.y } }
							: point,
					),
				},
			}));
		} else if (
			currentTool === "vacuum" &&
			getCurrentEyeState().vacuum.dragging &&
			getCurrentEyeState().vacuum.draggingIndex !== null
		) {
			const currentState = getCurrentEyeState();
			console.log(
				"Vacuum tool - dragging point:",
				currentState.vacuum.draggingIndex,
				"to:",
				coords.x,
				coords.y,
			); // Debug
			// Update the dragging point position
			setCurrentEyeState((prev) => ({
				...prev,
				vacuum: {
					...prev.vacuum,
					points: prev.vacuum.points.map((point, index) =>
						index === prev.vacuum.draggingIndex
							? { ...point, current: { x: coords.x, y: coords.y } }
							: point,
					),
				},
			}));
		} else if (ellipseRegionDragRef.current) {
			// F10/F11: live move/resize of the shared region ellipse.
			const ellipseTool = activeEllipseRegionTool();
			const drag = ellipseRegionDragRef.current;
			if (ellipseTool) {
				setEllipseRegion(ellipseTool, (r) => {
					if (!r) return r;
					return drag.mode === "move"
						? { ...r, cx: coords.x - drag.ox, cy: coords.y - drag.oy }
						: {
								...r,
								rx: Math.max(20, Math.abs(coords.x - r.cx)),
								ry: Math.max(20, Math.abs(coords.y - r.cy)),
							};
				});
			}
		}
	};

	const handleMouseUp = (e: React.MouseEvent | React.TouchEvent) => {
		// Clear touch flag after a delay to allow synthetic mouse events to be ignored
		if ("touches" in e || e.type === "touchend") {
			setTimeout(() => {
				touchActiveRef.current = false;
			}, 500);
		}

		// Handle panning
		if (isPanning) {
			setIsPanning(false);
			return;
		}

		if (isViewMode) return;

		// Stop dragging
		if (currentTool === "distortion") {
			// Stop dragging (F12: clear the slice pinned at drag start, so a
			// mid-drag gridMode switch can't strand dragging=true on it).
			const field = dragDistortionFieldRef.current;
			setCurrentEyeState((prev) => ({
				...prev,
				[field]: {
					...prev[field],
					dragging: false,
					draggingIndex: null,
				},
			}));
		} else if (currentTool === "vacuum") {
			// Stop dragging
			setCurrentEyeState((prev) => ({
				...prev,
				vacuum: {
					...prev.vacuum,
					dragging: false,
					draggingIndex: null,
				},
			}));
		} else if (ellipseRegionDragRef.current) {
			// F10/F11: end the shared region move/resize gesture.
			ellipseRegionDragRef.current = null;
		}
	};

	// Combined click handler for different tools
	// Write a polygon region to the current eye's tool state, then exit drawing mode.
	const closePolygon = (points: Array<{ x: number; y: number }>) => {
		if (points.length < 3) return;
		const mask = computePolygonMask(points, imageWidth, imageHeight);
		const region: EffectRegionPolygon = {
			type: "polygon",
			points,
			mask,
			maskWidth: imageWidth,
			maskHeight: imageHeight,
		};
		setCurrentEyeState((prev) => {
			if (currentTool === "blurriness") {
				return {
					...prev,
					blurriness: { ...prev.blurriness, effectRegion: region },
				};
			}
			if (currentTool === "contrast") {
				return {
					...prev,
					contrast: { ...prev.contrast, effectRegion: region },
				};
			}
			if (currentTool === "imageBrightness") {
				return {
					...prev,
					imageBrightness: { ...prev.imageBrightness, effectRegion: region },
				};
			}
			if (currentTool === "fieldDefect") {
				const stored: StoredFieldDefect = { mode: "tunnel", points };
				const fdMask = buildFieldDefectMask(stored, imageWidth, imageHeight);
				return {
					...prev,
					fieldDefect: fdMask
						? {
								...stored,
								mask: fdMask,
								maskWidth: imageWidth,
								maskHeight: imageHeight,
							}
						: null,
				};
			}
			return prev;
		});
		setRegionPoints([]);
		setRegionDrawMode("none");
		setLastTapTime(0);
	};

	// One tap during polygon drawing: append a vertex, OR close if conditions met.
	// Close conditions (need >= 3 prior points): double-tap (< 300 ms apart) OR
	// tap within 22 px of the first vertex in canvas space (zoom-corrected so the
	// affordance feels the same regardless of zoom).
	const handlePolygonTap = (coords: { x: number; y: number }) => {
		const now = Date.now();
		const first = regionPoints[0];
		const closeRadius = 22 / Math.max(0.5, imageZoom);
		const isDoubleTap = now - lastTapTime < 300 && regionPoints.length >= 3;
		const isCloseToStart =
			!!first &&
			regionPoints.length >= 3 &&
			Math.hypot(coords.x - first.x, coords.y - first.y) < closeRadius;
		if (isDoubleTap || isCloseToStart) {
			closePolygon(regionPoints);
			return;
		}
		setRegionPoints((prev) => [...prev, coords]);
		setLastTapTime(now);
	};

	const handleImageClick = (e: React.MouseEvent | React.TouchEvent) => {
		// Only handle mouse clicks, not touch events
		if ("touches" in e) return;
		// Ignore synthetic click events generated after touch interactions
		if (touchActiveRef.current) return;
		if (isViewMode) return;

		const coords = getEventCoordinates(e);
		if (!coords) return;

		// Polygon ROI drawing: tap to add points; double-tap or tap-near-start to close.
		if (regionDrawMode === "drawing") {
			handlePolygonTap(coords);
			return;
		}

		if (currentTool === "fieldDefect" && fieldDefectMode === "curtain") {
			if (!curtainStart) {
				setCurtainStart(coords);
				return;
			}
			// A degenerate line (both taps identical) would blacken the whole image;
			// drop the duplicate tap and wait for a distinct second point.
			if (curtainStart.x === coords.x && curtainStart.y === coords.y) return;
			const stored: StoredFieldDefect = {
				mode: "curtain",
				line: {
					x1: curtainStart.x,
					y1: curtainStart.y,
					x2: coords.x,
					y2: coords.y,
					side: "a",
				},
			};
			const fdMask = buildFieldDefectMask(stored, imageWidth, imageHeight);
			setCurrentEyeState((prev) => ({
				...prev,
				fieldDefect: fdMask
					? {
							...stored,
							mask: fdMask,
							maskWidth: imageWidth,
							maskHeight: imageHeight,
						}
					: null,
			}));
			setCurtainStart(null);
			return;
		}

		if (currentTool === "scotoma") {
			// Hit-test existing spots (topmost first). Clicking inside a spot selects
			// it for editing; clicking empty canvas places a new spot and selects it.
			const cur = getCurrentEyeState();
			let hitIndex: number | null = null;
			for (let i = cur.scotoma.length - 1; i >= 0; i--) {
				const s = cur.scotoma[i];
				if (!s) continue;
				if (Math.hypot(coords.x - s.x, coords.y - s.y) <= s.radius) {
					hitIndex = i;
					break;
				}
			}
			if (hitIndex !== null) {
				setSelectedScotomaIndex(hitIndex);
				return;
			}
			setCurrentEyeState((prev) => ({
				...prev,
				scotoma: [
					...prev.scotoma,
					{
						x: coords.x,
						y: coords.y,
						radius: DEFAULT_SCOTOMA_RADIUS,
						intensity: DEFAULT_SCOTOMA_OPACITY,
					},
				],
			}));
			setSelectedScotomaIndex(cur.scotoma.length);
		}
	};

	const handleTouchStart = (e: React.TouchEvent) => {
		console.log("handleTouchStart called, currentTool:", currentTool);

		// Mark that we're in a touch interaction
		touchActiveRef.current = true;

		if (isViewMode) return;

		if (regionDrawMode === "drawing") {
			// Suppress synthetic click event so handleImageClick doesn't double-process
			e.preventDefault();
			const coords = getEventCoordinates(e);
			if (!coords) return;
			handlePolygonTap(coords);
			return;
		}

		// For scotoma tool, use onClick instead to avoid double registration
		if (currentTool === "scotoma") {
			// Don't handle touch for scotoma - let onClick handle it
			return;
		}

		// For other tools, handle touch as mouse down
		handleMouseDown(e);
	};

	// Remove a scotoma spot on right-click
	const handleScotomaRightClick = (e: React.MouseEvent, idx: number) => {
		e.preventDefault();
		if (isViewMode) return;
		if (currentTool !== "scotoma") return;
		setCurrentEyeState((prev) => ({
			...prev,
			scotoma: prev.scotoma.filter((_, i) => i !== idx),
		}));
		// Keep the selection on the same spot after the removal shifts indices.
		setSelectedScotomaIndex((sel) =>
			sel === null ? null : sel === idx ? null : sel > idx ? sel - 1 : sel,
		);
	};

	// Helper to check if a filter is active
	const isFilterActive = (filterName: string) => {
		const currentState = getCurrentEyeState();
		switch (filterName) {
			case "imageBrightness":
				return (
					currentState.imageBrightness.value !== 1 ||
					(currentState.imageBrightness.roiValue ?? 1) !== 1
				);
			case "contrast":
				return (
					currentState.contrast.level !== 1 ||
					currentState.contrast.preset ||
					currentState.contrast.mode ||
					(currentState.contrast.roiLevel ?? 1) !== 1
				);
			case "colourShift":
				return (
					currentState.colourShift.hue !== 0 ||
					currentState.colourShift.saturation !== 1 ||
					currentState.colourShift.preset
				);
			case "rotation":
				return currentState.rotation.angle !== 0;
			case "diplopia":
				return currentState.diplopia.enabled;
			case "scotoma":
				return currentState.scotoma.length > 0;

			case "blurriness":
				return (
					currentState.blurriness.sigma > 0 ||
					(currentState.blurriness.roiSigma ?? 0) > 0
				);
			case "distortion":
				// F12: active if either the face or the Amsler slice has points.
				return (
					currentState.distortion.points.length > 0 ||
					currentState.amslerDistortion.points.length > 0
				);
			case "vacuum":
				return currentState.vacuum.points.length > 0;
			case "metamorphopsia":
				return (
					currentState.metamorphopsia.enabled &&
					(currentState.metamorphopsia.amplitude > 0 ||
						currentState.metamorphopsia.frequency > 0)
				);
			case "anisoconia":
				return (
					(currentState.anisoconia.mode !== "region" &&
						currentState.anisoconia.headScale !== 1) ||
					(currentState.anisoconia.mode === "region" &&
						currentState.anisoconia.region != null &&
						(currentState.anisoconia.scale ?? 1) !== 1)
				);
			default:
				return false;
		}
	};

	const toolsUsed = useMemo(() => {
		const activeTools = new Set<string>();
		if (currentTool) activeTools.add(currentTool);
		for (const tool of TOOL_LIST) {
			if (isFilterActive(tool.key)) {
				activeTools.add(tool.key);
			}
		}
		return activeTools;
	}, [currentTool, isFilterActive]);

	// Helper to get filter description - memoized to update when eye state changes
	const getFilterDescription = useCallback(
		(filterName: string) => {
			const currentState = getCurrentEyeState();
			switch (filterName) {
				case "imageBrightness": {
					const baseB = Math.round(currentState.imageBrightness.value * 100);
					const roiB = Math.round(
						(currentState.imageBrightness.roiValue ?? 1) * 100,
					);
					if (currentState.imageBrightness.value !== 1 && roiB !== 100) {
						return `Brightness: ${baseB}% + region ${roiB}%`;
					}
					if (roiB !== 100) return `Brightness: region ${roiB}%`;
					return `Brightness: ${baseB}%`;
				}
				case "contrast": {
					const roiL = currentState.contrast.roiLevel ?? 1;
					const labelBase = currentState.contrast.preset
						? currentState.contrast.preset
						: currentState.contrast.mode
							? currentState.contrast.mode
							: `${currentState.contrast.level}`;
					if (currentState.contrast.level !== 1 && roiL !== 1) {
						return `Contrast: ${labelBase} + region ${roiL}`;
					}
					if (roiL !== 1) return `Contrast: region ${roiL}`;
					return `Contrast: ${labelBase}`;
				}
				case "colourShift":
					if (currentState.colourShift.preset)
						return `Colour: ${currentState.colourShift.preset}`;
					return `Colour: H${currentState.colourShift.hue} S${currentState.colourShift.saturation}`;
				case "rotation":
					return `Rotation: ${currentState.rotation.angle}°`;
				case "diplopia":
					return `Diplopia: ${currentState.diplopia.overlap || 50}% overlap`;
				case "scotoma":
					return `Scotoma: ${currentState.scotoma.length} spots`;

				case "blurriness": {
					const baseS = currentState.blurriness.sigma;
					const roiS = currentState.blurriness.roiSigma ?? 0;
					if (baseS > 0 && roiS > 0) {
						return `Blur: σ${baseS} + region σ${roiS}`;
					}
					if (roiS > 0) return `Blur: region σ${roiS}`;
					return `Blur: σ${baseS}`;
				}
				case "distortion": {
					// F12: describe whichever slice has points (face takes priority).
					const d =
						currentState.distortion.points.length > 0
							? currentState.distortion
							: currentState.amslerDistortion;
					const distortionDesc = `Distortion: ${d.points.length} points`;
					const powerDesc = d.power !== 1.0 ? ` P${d.power}` : "";
					const rangeDesc = d.range !== 100 ? ` R${d.range}` : "";
					return `${distortionDesc}${powerDesc}${rangeDesc}`;
				}
				case "vacuum": {
					const vacuumDesc = `Vacuum: ${currentState.vacuum.points.length} points`;
					const vacuumPowerDesc =
						currentState.vacuum.power !== 1.0
							? ` P${currentState.vacuum.power}`
							: "";
					const vacuumRangeDesc =
						currentState.vacuum.range !== 100
							? ` R${currentState.vacuum.range}`
							: "";
					return `${vacuumDesc}${vacuumPowerDesc}${vacuumRangeDesc}`;
				}
				case "metamorphopsia":
					return `Metamorphopsia${currentState.metamorphopsia.mode === "region" ? " (region)" : ""}: A${currentState.metamorphopsia.amplitude} F${currentState.metamorphopsia.frequency}`;
				case "anisoconia":
					return currentState.anisoconia.mode === "region"
						? `Anisoconia (region): ${Math.round((currentState.anisoconia.scale ?? 1) * 100)}%`
						: `Anisoconia: ${currentState.anisoconia.headScale * 100}%`;
				default:
					return filterName;
			}
		},
		[eyeStates, eye],
	);

	// Toggle filter enable/disable
	const toggleFilter = useCallback((filterName: string) => {
		setDisabledFilters((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(filterName)) {
				newSet.delete(filterName);
			} else {
				newSet.add(filterName);
			}
			return newSet;
		});
	}, []);

	// Remove filter
	const removeFilter = useCallback(
		(filterName: string) => {
			setCurrentEyeState((prev) => {
				switch (filterName) {
					case "imageBrightness":
						return {
							...prev,
							imageBrightness: { value: 1, effectRegion: null },
						};
					case "contrast":
						return {
							...prev,
							contrast: {
								level: 1,
								preset: undefined,
								mode: undefined,
								effectRegion: null,
							},
						};
					case "colourShift":
						return {
							...prev,
							colourShift: { hue: 0, saturation: 1, preset: undefined },
						};
					case "rotation":
						return { ...prev, rotation: { angle: 0 } };
					case "diplopia":
						return { ...prev, diplopia: { enabled: false } };
					case "scotoma":
						return { ...prev, scotoma: [] };
					case "blurriness":
						return {
							...prev,
							blurriness: { sigma: 0, effectRegion: null },
						};
					case "distortion":
						// F12: reset clears only the active slice (Amsler vs face).
						return {
							...prev,
							[distortionField]: {
								points: [],
								draggingIndex: null,
								dragging: false,
								power: 1.0,
								range: 100,
								vacuumEffect: false,
								showImage: true,
							},
						};
					case "vacuum":
						return {
							...prev,
							vacuum: {
								points: [],
								draggingIndex: null,
								dragging: false,
								power: 1.0,
								range: 100,
							},
						};
					case "metamorphopsia":
						return {
							...prev,
							metamorphopsia: {
								enabled: false,
								amplitude: 0,
								frequency: 0,
								direction: "horizontal",
								mode: "whole",
								region: null,
								bridge: 0.5,
							},
						};
					case "anisoconia":
						return {
							...prev,
							anisoconia: {
								...prev.anisoconia,
								headScale: 1,
								mode: "whole",
								region: null,
								scale: 1,
							},
						};
					default:
						return prev;
				}
			});
			setDisabledFilters((prev) => {
				const newSet = new Set(prev);
				newSet.delete(filterName);
				return newSet;
			});
		},
		[setCurrentEyeState],
	);

	// Get active filters
	const activeFilters = [
		"imageBrightness",
		"contrast",
		"colourShift",
		"rotation",
		"diplopia",
		"scotoma",

		"blurriness",
		"distortion",
		"vacuum",
		"metamorphopsia",
		"anisoconia",
	].filter(isFilterActive);

	// --- CSV Export Helper ---
	function exportSessionCSV() {
		// `dateStr` (YYYY-MM-DD) feeds the existing `date` prefix column on
		// every row. In view mode it reflects when the assessment was created;
		// in edit mode it reflects today.
		const dateStr =
			isViewMode && viewDate
				? new Date(viewDate).toISOString().slice(0, 10)
				: new Date().toISOString().slice(0, 10);

		// `dateTimeForFilename` (YYYY-MM-DD_HH-MM) disambiguates multiple
		// exports of the same assessment on the same day. Always uses the
		// moment of export so the filename is unique per export action.
		const exportNow = new Date();
		const pad = (n: number) => String(n).padStart(2, "0");
		const dateTimeForFilename = `${exportNow.getFullYear()}-${pad(exportNow.getMonth() + 1)}-${pad(exportNow.getDate())}_${pad(exportNow.getHours())}-${pad(exportNow.getMinutes())}`;
		const exportedAtIso = exportNow.toISOString();

		// NOTE: practitioner uses the current user's name. In view mode this may
		// differ from the original practitioner because that name is not stored
		// on the assessment record.
		const practitioner = user?.fullName ?? user?.firstName ?? "";
		const providerVal = patient?.provider ?? "";
		const siteVal = patient?.site ?? "";
		const rows: string[] = [];

		// Escape CSV values that contain commas, quotes, or newlines
		const esc = (v: string | number | boolean): string => {
			const s = String(v);
			if (s.includes(",") || s.includes('"') || s.includes("\n")) {
				return `"${s.replace(/"/g, '""')}"`;
			}
			return s;
		};

		const push: (
			tool: string,
			param: string,
			value: string | number | boolean,
		) => void = (tool, param, value) => {
			rows.push(
				[
					patientCode,
					practitioner,
					providerVal,
					siteVal,
					eye,
					dateStr,
					tool,
					param,
					value,
				]
					.map(esc)
					.join(","),
			);
		};

		// Metadata rows (always emit at the top of the data block).
		// exportVersion: which version of the export naming + schema this file
		//                follows. Parsers branch on this. See
		//                specs/csv-export-schema.md.
		// exportedAt: ISO timestamp of when this CSV was generated. Distinct
		//             from `date` column (which is the assessment creation date
		//             in view mode, or today in edit mode).
		// assessmentId: only emits when viewing a saved assessment, so
		//               downstream tools can link the file back to the server
		//               record.
		push("metadata", "exportVersion", EXPORT_SCHEMA_VERSION);
		push("metadata", "exportedAt", exportedAtIso);
		if (assessmentId) {
			push("metadata", "assessmentId", assessmentId);
		}

		// Get current eye state for all filter data
		const currentState = getCurrentEyeState();

		// Distortion
		if (currentState.distortion.points.length > 0) {
			currentState.distortion.points.forEach((p, i) => {
				push("distortion", `point${i + 1}_original_x`, p.original.x);
				push("distortion", `point${i + 1}_original_y`, p.original.y);
				push("distortion", `point${i + 1}_current_x`, p.current.x);
				push("distortion", `point${i + 1}_current_y`, p.current.y);
			});
			push("distortion", "power", currentState.distortion.power);
			push("distortion", "range", currentState.distortion.range);
		}

		// F12: Amsler grid distortion (separate slice from the face above).
		if (currentState.amslerDistortion.points.length > 0) {
			currentState.amslerDistortion.points.forEach((p, i) => {
				push("amsler-distortion", `point${i + 1}_original_x`, p.original.x);
				push("amsler-distortion", `point${i + 1}_original_y`, p.original.y);
				push("amsler-distortion", `point${i + 1}_current_x`, p.current.x);
				push("amsler-distortion", `point${i + 1}_current_y`, p.current.y);
			});
			push("amsler-distortion", "power", currentState.amslerDistortion.power);
			push("amsler-distortion", "range", currentState.amslerDistortion.range);
		}

		// Vacuum
		if (currentState.vacuum.points.length > 0) {
			currentState.vacuum.points.forEach((p, i) => {
				push("vacuum", `point${i + 1}_original_x`, p.original.x);
				push("vacuum", `point${i + 1}_original_y`, p.original.y);
				push("vacuum", `point${i + 1}_current_x`, p.current.x);
				push("vacuum", `point${i + 1}_current_y`, p.current.y);
			});
			push("vacuum", "power", currentState.vacuum.power);
			push("vacuum", "range", currentState.vacuum.range);
		}

		// Image Brightness: always emit base for parity with always-emit roi.
		push(
			"imageBrightness",
			"magnification",
			currentState.imageBrightness.value,
		);

		// Contrast: always emit base level for parity with always-emit roi.
		// Preset and mode still conditional (only when set).
		push("contrast", "level", currentState.contrast.level);
		if (currentState.contrast.preset)
			push("contrast", "preset", currentState.contrast.preset);
		if (currentState.contrast.mode)
			push("contrast", "mode", currentState.contrast.mode);

		// Colour Shift
		if (
			currentState.colourShift.hue !== 0 ||
			currentState.colourShift.saturation !== 1 ||
			currentState.colourShift.preset
		) {
			push("colourShift", "hue", currentState.colourShift.hue);
			push("colourShift", "saturation", currentState.colourShift.saturation);
			if (currentState.colourShift.preset)
				push("colourShift", "preset", currentState.colourShift.preset);
		}

		// Rotation
		if (currentState.rotation.angle !== 0) {
			push("rotation", "angle", currentState.rotation.angle);
		}

		// Diplopia
		if (currentState.diplopia.enabled) {
			push("diplopia", "enabled", currentState.diplopia.enabled);
			if (currentState.diplopia.offset) {
				push("diplopia", "offset_x", currentState.diplopia.offset.x);
				push("diplopia", "offset_y", currentState.diplopia.offset.y);
			}
			if (currentState.diplopia.overlap)
				push("diplopia", "overlap", currentState.diplopia.overlap);
		}

		// Metamorphopsia
		if (currentState.metamorphopsia.enabled) {
			push("metamorphopsia", "enabled", currentState.metamorphopsia.enabled);
			push(
				"metamorphopsia",
				"amplitude",
				currentState.metamorphopsia.amplitude,
			);
			push(
				"metamorphopsia",
				"frequency",
				currentState.metamorphopsia.frequency,
			);
			push(
				"metamorphopsia",
				"direction",
				currentState.metamorphopsia.direction,
			);
			// F11: region waviness. Additive rows; coords not shown in the UI.
			if (
				currentState.metamorphopsia.mode === "region" &&
				currentState.metamorphopsia.region
			) {
				const rgn = currentState.metamorphopsia.region;
				push(
					"metamorphopsia",
					"regionBridge",
					currentState.metamorphopsia.bridge ?? 0.5,
				);
				push("metamorphopsia", "regionCx", Math.round(rgn.cx));
				push("metamorphopsia", "regionCy", Math.round(rgn.cy));
				push("metamorphopsia", "regionRx", Math.round(rgn.rx));
				push("metamorphopsia", "regionRy", Math.round(rgn.ry));
			}
		}

		// Scotoma
		if (currentState.scotoma.length > 0) {
			currentState.scotoma.forEach((spot, i) => {
				push("scotoma", `spot${i + 1}_x`, spot.x);
				push("scotoma", `spot${i + 1}_y`, spot.y);
				push(
					"scotoma",
					`spot${i + 1}_area`,
					Math.round(Math.PI * spot.radius ** 2),
				);
				push("scotoma", `spot${i + 1}_opacity`, spot.intensity);
			});
		}

		// Blurriness: always emit base sigma for parity with always-emit roi.
		push("blurriness", "sigma", currentState.blurriness.sigma);

		// Effect Regions (per-tool). Rect: existing 4 rows. Polygon: type + JSON points.
		const pushRegion = (tool: string, r: EffectRegion | null | undefined) => {
			if (!r) return;
			if (r.type === "polygon") {
				push(tool, "effectRegion_type", "polygon");
				push(tool, "effectRegion_points", JSON.stringify(r.points));
				return;
			}
			push(tool, "effectRegion_x1", r.x1);
			push(tool, "effectRegion_y1", r.y1);
			push(tool, "effectRegion_x2", r.x2);
			push(tool, "effectRegion_y2", r.y2);
		};
		pushRegion(
			"blurriness",
			currentState.blurriness.effectRegion as EffectRegion | null,
		);
		pushRegion(
			"contrast",
			currentState.contrast.effectRegion as EffectRegion | null,
		);
		pushRegion(
			"imageBrightness",
			currentState.imageBrightness.effectRegion as EffectRegion | null,
		);

		// F6 ROI boost values (additive). Always emitted so CSV columns stay
		// stable across assessments regardless of region presence.
		push("blurriness", "roiSigma", currentState.blurriness.roiSigma ?? 0);
		push("contrast", "roiLevel", currentState.contrast.roiLevel ?? 1);
		push(
			"imageBrightness",
			"roiValue",
			currentState.imageBrightness.roiValue ?? 1,
		);

		// Anisoconia (whole head)
		if (
			currentState.anisoconia.mode !== "region" &&
			currentState.anisoconia.headScale !== 1
		) {
			push("anisoconia", "headScale", currentState.anisoconia.headScale);
		}

		// F10: region scale (regional micropsia/macropsia). Coordinates are not
		// shown in the UI but are recorded here for the clinical/research record.
		// Additive params under an existing tool → no CSV version bump.
		if (
			currentState.anisoconia.mode === "region" &&
			currentState.anisoconia.region
		) {
			const rgn = currentState.anisoconia.region;
			push("anisoconia", "regionScale", currentState.anisoconia.scale ?? 1);
			push("anisoconia", "regionBridge", currentState.anisoconia.bridge ?? 0.5);
			push("anisoconia", "regionCx", Math.round(rgn.cx));
			push("anisoconia", "regionCy", Math.round(rgn.cy));
			push("anisoconia", "regionRx", Math.round(rgn.rx));
			push("anisoconia", "regionRy", Math.round(rgn.ry));
		}

		const csv = [
			"patientCode,practitioner,provider,site,eye,date,tool,param,value",
			...rows,
		].join("\n");
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${patientCode}_${eye}_${dateTimeForFilename}_${EXPORT_SCHEMA_VERSION}.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	// --- Image Export Helper ---

	// Add refs for distortion and blur overlay canvases
	const distortionCanvasRef = useRef<HTMLCanvasElement>(null);
	const blurCanvasRef = useRef<HTMLCanvasElement>(null);

	function exportSessionImage() {
		const dateStr = new Date().toISOString().slice(0, 10);
		const visibleCanvas = visibleCanvasRef.current;
		const distortionOverlay = distortionCanvasRef.current;
		const blurOverlay = blurCanvasRef.current;

		// Use actual canvas size
		const width = visibleCanvas?.width || 1024;
		const height = visibleCanvas?.height || 1024;

		const exportCanvas = document.createElement("canvas");
		exportCanvas.width = width;
		exportCanvas.height = height;
		const ctx = exportCanvas.getContext("2d", { willReadFrequently: true });
		if (!ctx || !visibleCanvas) {
			alert("No canvas found to export.");
			return;
		}

		// Draw base image
		ctx.drawImage(visibleCanvas, 0, 0, width, height);
		// Draw overlays in visual stacking order
		if (distortionOverlay)
			ctx.drawImage(distortionOverlay, 0, 0, width, height);
		if (blurOverlay) ctx.drawImage(blurOverlay, 0, 0, width, height);

		const link = document.createElement("a");
		link.download = `${patientCode}_${eye}_${dateStr}.png`;
		link.href = exportCanvas.toDataURL("image/png");
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	// Format date as 'Month, Day #, Year'
	const formattedDate = (() => {
		const d = new Date(date);
		const month = d.toLocaleString("default", { month: "long" });
		const day = d.getDate();
		const year = d.getFullYear();
		return `${month}, ${day}, ${year}`;
	})();

	// Add responsive scaling for iPad Air and tablets
	const [canvasDimensions, setCanvasDimensions] = useState({
		width: 1024,
		height: 1024,
	});

	// Image zoom level (separate from canvas dimensions)
	const [imageZoom, setImageZoom] = useState(1); // 0.5x to 2.0x

	// Pan offset for zoomed images (allows dragging when zoomed in)
	const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
	const [isPanning, setIsPanning] = useState(false);
	const [panStart, setPanStart] = useState({ x: 0, y: 0 });

	// Track if we're in a touch interaction to prevent duplicate mouse events
	const touchActiveRef = useRef(false);

	// Calculate responsive dimensions for iPad Air (11-inch) and other tablets
	useEffect(() => {
		const calculateResponsiveDimensions = () => {
			const screenWidth = window.innerWidth;
			const screenHeight = window.innerHeight;

			// iPad Air 11-inch has 1640x2360 resolution in portrait
			// We want to ensure the image fits comfortably on screen
			const maxWidth = Math.min(screenWidth - 100, 800); // Leave space for sidebar and padding
			const maxHeight = Math.min(screenHeight - 200, 800); // Leave space for header and controls

			// Calculate scale to fit within these bounds while maintaining aspect ratio
			const scale = Math.min(maxWidth / 1024, maxHeight / 1024);

			setCanvasDimensions({
				width: 1024 * scale,
				height: 1024 * scale,
			});

			// Debug log for tablet responsiveness
			console.log("Responsive dimensions:", {
				screenWidth,
				screenHeight,
				maxWidth,
				maxHeight,
				scale,
				canvasWidth: 1024 * scale,
				canvasHeight: 1024 * scale,
			});
		};

		calculateResponsiveDimensions();
		window.addEventListener("resize", calculateResponsiveDimensions);

		return () =>
			window.removeEventListener("resize", calculateResponsiveDimensions);
	}, []);

	// Zoom control functions
	const handleZoomIn = () => {
		setImageZoom((prev) => Math.min(prev + 0.1, 2.0));
		setPanOffset({ x: 0, y: 0 }); // Reset pan when zoom changes
	};

	const handleZoomOut = () => {
		setImageZoom((prev) => Math.max(prev - 0.1, 0.5));
		setPanOffset({ x: 0, y: 0 }); // Reset pan when zoom changes
	};

	const handleResetZoom = () => {
		setImageZoom(1);
		setPanOffset({ x: 0, y: 0 }); // Reset pan when zoom resets
	};

	const handleReset = () => {
		if (
			!window.confirm(
				"Reset all tools to defaults? This will clear your current work.",
			)
		)
			return;
		if (patient?.id) {
			localStorage.removeItem(`assessment_wip_${patient.id}`);
		}
		// F8: make Reset undoable — snapshot the pre-reset state first.
		pushUndoSnapshot();
		setEyeStates({
			left: { ...DEFAULT_EYE_STATE },
			right: { ...DEFAULT_EYE_STATE },
		});
		// Selection indexes into the (now-cleared) per-eye spot array; reset it.
		setSelectedScotomaIndex(null);
	};

	async function handleSave() {
		if (!patient?.id) return;

		setIsSaving(true);
		try {
			const token = await getToken();

			const stripped = {
				left: stripEyeRegions({
					...eyeStates.left,
					distortion: {
						...eyeStates.left.distortion,
						dragging: undefined,
						draggingIndex: undefined,
						showImage: undefined,
					},
					amslerDistortion: {
						...eyeStates.left.amslerDistortion,
						dragging: undefined,
						draggingIndex: undefined,
						showImage: undefined,
					},
					vacuum: {
						...eyeStates.left.vacuum,
						dragging: undefined,
						draggingIndex: undefined,
					},
				}),
				right: stripEyeRegions({
					...eyeStates.right,
					distortion: {
						...eyeStates.right.distortion,
						dragging: undefined,
						draggingIndex: undefined,
						showImage: undefined,
					},
					amslerDistortion: {
						...eyeStates.right.amslerDistortion,
						dragging: undefined,
						draggingIndex: undefined,
						showImage: undefined,
					},
					vacuum: {
						...eyeStates.right.vacuum,
						dragging: undefined,
						draggingIndex: undefined,
					},
				}),
			};

			const res = await fetch(
				`${import.meta.env.VITE_BASE_API_URL}/assessments`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						patientId: patient.id,
						eyeStates: stripped,
					}),
				},
			);

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || "Failed to save assessment");
			}

			// Clear localStorage WIP after successful server save
			localStorage.removeItem(`assessment_wip_${patient.id}`);
			toast.success("Assessment saved");
		} catch (_err) {
			toast.error("Could not save to server. Your work is stored locally.", {
				action: {
					label: "Retry",
					onClick: () => handleSave(),
				},
			});
		} finally {
			setIsSaving(false);
		}
	}

	// Note: Removed manual touch event listeners to avoid conflicts with React event handlers

	// Patient guard - redirect to /patients if no patient selected and not viewing a saved assessment
	useEffect(() => {
		if (authLoading) return;
		if (!user) return;
		if (!patient && !assessmentId) {
			navigate("/patients");
		}
	}, [authLoading, user, patient, assessmentId, navigate]);

	// Auth guard - redirect to login if not authenticated (AFTER all hooks are declared)
	if (authLoading) {
		return (
			<div className="flex items-center justify-center h-screen">
				<div className="text-gray-600">Loading...</div>
			</div>
		);
	}

	if (!user) {
		navigate("/login");
		return null;
	}

	if (!patient && !assessmentId) {
		return null;
	}

	return (
		<>
			{/* Portrait Mode Overlay */}
			{isPortrait && (
				<div className="fixed inset-0 z-50 bg-blue-600 flex items-center justify-center p-4">
					<div className="bg-white rounded-xl shadow-2xl p-12 max-w-lg text-center">
						<div className="mb-8">
							<svg
								className="w-24 h-24 mx-auto text-blue-600 mb-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								role="img"
								aria-label="Rotate device icon"
							>
								<title>Rotate device icon</title>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
								/>
							</svg>
						</div>
						<h2 className="text-4xl font-bold text-gray-800 mb-6">
							Please Rotate Your Device
						</h2>
						<p className="text-xl text-gray-600 mb-8 leading-relaxed">
							This app is optimized for landscape mode. Please rotate your
							device to continue.
						</p>
						<div className="text-lg text-gray-500 font-medium">
							For the best experience, use this app in landscape orientation.
						</div>
					</div>
				</div>
			)}

			<div className="flex min-h-screen w-screen">
				{/* Sidebar */}
				<aside className="flex flex-col border-r bg-white w-48 lg:w-56 p-3 lg:p-4">
					<div className="flex flex-col items-center justify-center mb-4 w-full">
						<div className="flex flex-col items-center gap-2 w-full">
							<img
								src="/images/company-logo.png"
								alt="Unity Health - St. Michael's"
								className="transition-opacity duration-200 w-full h-auto max-h-18 object-contain"
							/>
						</div>
					</div>
					<ul className="flex-1 space-y-4 overflow-y-auto max-h-full">
						{TOOL_LIST.map((tool) => {
							const Icon = tool.icon;
							// Inline style for background color
							const isSelected = currentTool === tool.key;
							const style = isSelected
								? { background: "#bfdbfe" } // Tailwind's bg-blue-200
								: {};
							return (
								<li key={tool.key}>
									<button
										style={style}
										className={
											"w-full flex items-center px-3 py-2 rounded transition font-medium gap-3 text-gray-800"
										}
										onClick={() => {
											// Switching tools mid-polygon-draw must abort the draw
											// or the next canvas tap silently appends a vertex to
											// the previous tool's region instead of acting on the
											// new tool (e.g. placing a scotoma spot).
											setRegionDrawMode("none");
											setRegionPoints([]);
											setLastTapTime(0);
											setCurtainStart(null);
											ellipseRegionDragRef.current = null;
											setCurrentTool(tool.key as ToolKey);
										}}
									>
										<Icon className="w-6 h-6" />
										<span className="flex-1 text-left">{tool.label}</span>
										{tool.hasRegion && (
											<span
												className="text-blue-500 text-sm leading-none"
												title="Has a region mode (apply to a chosen area)"
												aria-label="Has a region mode"
											>
												◐
											</span>
										)}
									</button>
								</li>
							);
						})}
					</ul>
				</aside>

				{/* Main Content */}
				<main className="flex flex-col items-center justify-start p-2 lg:p-3 overflow-hidden xl:overflow-y-auto flex-1 h-screen xl:h-auto xl:min-h-screen relative">
					{/* Navbar (Top) */}
					<SessionToolbar
						patientCode={patientCode}
						formattedDate={formattedDate}
						eye={eye}
						onEyeChange={(nextEye) => {
							// Selection is eye-scoped; clear it so the index can't
							// point into the other eye's spot array.
							setEye(nextEye);
							setSelectedScotomaIndex(null);
							setCurtainStart(null);
							ellipseRegionDragRef.current = null;
						}}
						gridMode={gridMode}
						onGridModeChange={setGridMode}
						gridLineWidth={gridLineWidth}
						onGridLineWidthChange={setGridLineWidth}
						imageZoom={imageZoom}
						onZoomIn={handleZoomIn}
						onZoomOut={handleZoomOut}
						onResetZoom={handleResetZoom}
						onSave={handleSave}
						isSaving={isSaving}
						onExportCSV={exportSessionCSV}
						onExportImage={exportSessionImage}
						onBack={() => navigate("/patients")}
						onReset={handleReset}
						onUndo={handleUndo}
						canUndo={canUndo}
						isViewMode={isViewMode}
						viewDate={viewDate}
						onToggleViewMode={handleToggleViewMode}
					/>

					{/* Canvas Area */}
					<div className="w-full h-full flex flex-col lg:flex-row bg-white rounded relative p-2 gap-2 tablet-responsive">
						{/* Reference Image Placeholder */}
						{!imageLoaded && (
							<div
								className="flex items-center justify-center text-lg text-gray-400"
								style={{
									width: `${canvasDimensions.width}px`,
									height: `${canvasDimensions.height}px`,
								}}
							>
								Loading image...
							</div>
						)}
						{imageLoaded && (
							<div
								ref={imageContainerRef}
								className="relative select-none canvas-container"
								style={{
									width: `${canvasDimensions.width}px`,
									height: `${canvasDimensions.height}px`,
									overflow: "hidden", // Clip zoomed content to keep layout stable
									flexShrink: 0, // Prevent flex from shrinking the canvas
									touchAction: "none", // Disable default touch behavior to prevent synthetic mouse events
								}}
								onMouseDown={handleMouseDown}
								onMouseMove={handleMouseMove}
								onMouseUp={handleMouseUp}
								onClick={handleImageClick}
								onTouchStart={handleTouchStart}
								onTouchMove={handleMouseMove}
								onTouchEnd={handleMouseUp}
							>
								{/* Eye indicator badge */}
								<div className="absolute top-2 left-2 z-30 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow pointer-events-none select-none">
									{eye === "left" ? "Left Eye" : "Right Eye"}
								</div>
								{/* Visible canvas showing the filtered image */}
								<canvas
									ref={visibleCanvasRef}
									width={imageWidth}
									height={imageHeight}
									className="absolute z-10 select-none object-contain"
									style={{
										width: `${canvasDimensions.width * imageZoom}px`,
										height: `${canvasDimensions.height * imageZoom}px`,
										top: `${(canvasDimensions.height - canvasDimensions.height * imageZoom) / 2 + panOffset.y}px`,
										left: `${(canvasDimensions.width - canvasDimensions.width * imageZoom) / 2 + panOffset.x}px`,
										transition: isPanning ? "none" : "all 0.2s ease-in-out",
										cursor:
											regionDrawMode === "drawing"
												? "crosshair"
												: imageZoom > 1 && currentTool === null
													? "grab"
													: "default",
									}}
									draggable={false}
								/>
								{/* Distortion canvas overlay */}
								{(distortion.points.length > 0 || showGrid) &&
									!disabledFilters.has("distortion") && (
										<canvas
											ref={distortionCanvasRef}
											className="absolute pointer-events-none z-10"
											width={imageWidth}
											height={imageHeight}
											style={{
												width: `${canvasDimensions.width * imageZoom}px`,
												height: `${canvasDimensions.height * imageZoom}px`,
												top: `${(canvasDimensions.height - canvasDimensions.height * imageZoom) / 2 + panOffset.y}px`,
												left: `${(canvasDimensions.width - canvasDimensions.width * imageZoom) / 2 + panOffset.x}px`,
												transition: isPanning ? "none" : "all 0.2s ease-in-out",
											}}
										/>
									)}
								{/* Effect Region overlay: in-progress polyline + vertices, OR closed polygon, OR legacy rect */}
								{(() => {
									const es = getCurrentEyeState();
									const region: EffectRegion | null =
										currentTool === "blurriness"
											? (es.blurriness.effectRegion as EffectRegion | null)
											: currentTool === "contrast"
												? (es.contrast.effectRegion as EffectRegion | null)
												: currentTool === "imageBrightness"
													? (es.imageBrightness
															.effectRegion as EffectRegion | null)
													: null;
									const drawing = regionDrawMode === "drawing";
									if (!region && !drawing) return null;
									return (
										<svg
											className="absolute pointer-events-none z-20"
											style={{
												width: `${canvasDimensions.width * imageZoom}px`,
												height: `${canvasDimensions.height * imageZoom}px`,
												top: `${(canvasDimensions.height - canvasDimensions.height * imageZoom) / 2 + panOffset.y}px`,
												left: `${(canvasDimensions.width - canvasDimensions.width * imageZoom) / 2 + panOffset.x}px`,
												transition: isPanning ? "none" : "all 0.2s ease-in-out",
											}}
											viewBox={`0 0 ${imageWidth} ${imageHeight}`}
										>
											{drawing && regionPoints.length > 0 && (
												<>
													<polyline
														points={regionPoints
															.map((p) => `${p.x},${p.y}`)
															.join(" ")}
														fill="none"
														stroke="rgba(59, 130, 246, 0.85)"
														strokeWidth={3}
														strokeDasharray="12 6"
													/>
													{regionPoints.map((p, i) => (
														<circle
															key={`${p.x}-${p.y}-${i}`}
															cx={p.x}
															cy={p.y}
															r={22}
															fill="rgba(59, 130, 246, 0.4)"
															stroke="rgba(59, 130, 246, 1)"
															strokeWidth={2.5}
														/>
													))}
												</>
											)}
											{region?.type === "polygon" && (
												<polygon
													points={region.points
														.map((p) => `${p.x},${p.y}`)
														.join(" ")}
													fill="rgba(59, 130, 246, 0.12)"
													stroke="rgba(59, 130, 246, 0.85)"
													strokeWidth={3}
													strokeDasharray="12 6"
												/>
											)}
											{region && region.type !== "polygon" && (
												<rect
													x={region.x1}
													y={region.y1}
													width={region.x2 - region.x1}
													height={region.y2 - region.y1}
													fill="none"
													stroke="rgba(59, 130, 246, 0.8)"
													strokeWidth={3}
													strokeDasharray="12 6"
												/>
											)}
										</svg>
									);
								})()}
								{/* F10/F11: shared region ellipse overlay (anisoconia +
								    metamorphopsia, region mode only) */}
								{(() => {
									const ellipseTool = activeEllipseRegionTool();
									const es = getCurrentEyeState();
									const sub = ellipseTool ? es[ellipseTool] : null;
									if (!sub || !sub.region) return null;
									const r = sub.region;
									const inner = Math.max(0, 1 - (sub.bridge ?? 0.5));
									return (
										<svg
											className="absolute pointer-events-none z-20"
											style={{
												width: `${canvasDimensions.width * imageZoom}px`,
												height: `${canvasDimensions.height * imageZoom}px`,
												top: `${(canvasDimensions.height - canvasDimensions.height * imageZoom) / 2 + panOffset.y}px`,
												left: `${(canvasDimensions.width - canvasDimensions.width * imageZoom) / 2 + panOffset.x}px`,
												transition: isPanning ? "none" : "all 0.2s ease-in-out",
											}}
											viewBox={`0 0 ${imageWidth} ${imageHeight}`}
										>
											<ellipse
												cx={r.cx}
												cy={r.cy}
												rx={r.rx}
												ry={r.ry}
												fill="none"
												stroke="rgba(79, 70, 229, 0.95)"
												strokeWidth={3}
												strokeDasharray="12 9"
											/>
											{inner > 0.02 && (
												<ellipse
													cx={r.cx}
													cy={r.cy}
													rx={r.rx * inner}
													ry={r.ry * inner}
													fill="none"
													stroke="rgba(79, 70, 229, 0.4)"
													strokeWidth={2}
												/>
											)}
											<circle
												cx={r.cx + r.rx}
												cy={r.cy + r.ry}
												r={16}
												fill="rgba(79, 70, 229, 1)"
												stroke="#fff"
												strokeWidth={3}
											/>
										</svg>
									);
								})()}
							</div>
						)}

						{/* Tool Controls and Active Filters Column */}
						<div
							className="flex flex-col gap-2 lg:top-4 tool-controls-scrollable"
							style={{
								width: "100%",
								maxWidth: "100%",
								height: `${canvasDimensions.height}px`, // Match canvas height
								alignSelf: "flex-start",
								overflow: "hidden", // Hide overflow to contain scrollable children
							}}
						>
							{/* Combined Tool Controls and Active Filters Container */}
							<div
								className="flex flex-col h-full bg-gray-50 rounded shadow p-2"
								style={{
									boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
									overflow: "hidden",
								}}
							>
								{/* Tool Controls Section - scrollable */}
								{currentTool ? (
									<div className="flex-1 min-h-0 mb-4 pb-4 border-b border-gray-200 overflow-y-auto">
										{renderTool()}
									</div>
								) : (
									<div className="px-2 py-3 mb-4 text-xs text-dim text-center">
										Select a tool from the sidebar
									</div>
								)}

								{/* Active Filters Section - scrollable */}
								<div className="flex-1 min-h-0 pt-2">
									<ActiveFilters
										activeFilters={activeFilters}
										disabledFilters={disabledFilters}
										getFilterDescription={getFilterDescription}
										toggleFilter={toggleFilter}
										removeFilter={removeFilter}
										currentTool={currentTool}
									/>
								</div>
							</div>
						</div>

						{/* Add the hidden canvases to the DOM (just before the overlays) */}
						<canvas
							ref={filteredCanvasRef}
							width={imageWidth}
							height={imageHeight}
							style={{ display: "none" }}
						/>
						<canvas
							ref={originalImageCanvasRef}
							width={imageWidth}
							height={imageHeight}
							style={{ display: "none" }}
						/>
					</div>
				</main>
			</div>
		</>
	);
}

function AnisoconiaTool({
	headScale,
	onChange,
}: {
	headScale: number;
	onChange: (v: { headScale: number }) => void;
}) {
	return (
		<div className="space-y-4">
			<div className="text-sm text-gray-600 mb-4">
				<p>
					Adjust the head size using the slider below. The body remains
					unchanged for reference.
				</p>
			</div>
			<div>
				<label
					htmlFor="anisoconia-head-size"
					className="block text-sm font-medium mb-1"
				>
					Head Size: {(headScale * 100).toFixed(0)}%
				</label>
				<input
					id="anisoconia-head-size"
					type="range"
					min={0.7}
					max={1.3}
					step={0.01}
					value={headScale}
					onChange={(e) =>
						onChange({ headScale: Number.parseFloat(e.target.value) })
					}
					className="w-full"
				/>
			</div>
		</div>
	);
}
